/**
 * Admin Console - Main Entry Point
 * 
 * Initializes the app, handles routing, and manages view switching.
 */

import './style.css';
import { initAuth, checkExistingAuth, logout } from './auth';
import { api } from './api';
import { initDashboard, stopDashboard, refreshDashboard } from './dashboard';
import { initLogs, stopLogs } from './logs';
import { initTraces, refreshTraces } from './traces';
import { initStorage, refreshStorage } from './storage';
import { initUpdates } from './updates';
import { initOrganizations, refreshOrganizations } from './organizations';

// ============================================================================
// State
// ============================================================================

type Panel = 'dashboard' | 'logs' | 'traces' | 'storage' | 'organizations' | 'updates';
let currentPanel: Panel = 'dashboard';

// DOM Elements
const authModal = document.getElementById('auth-modal')!;
const mainApp = document.getElementById('main-app')!;

// ============================================================================
// Initialization
// ============================================================================

document.addEventListener('DOMContentLoaded', async () => {
    // Check for SSO hash fragment from Anubis webapp (#auth=<key>&uid=<id>)
    const ssoHandled = await handleSSOFragment();

    if (ssoHandled || checkExistingAuth()) {
        showApp();
    } else {
        showAuth();
    }

    // Initialize auth handlers (always, for login flow)
    initAuth();

    // Auth events
    window.addEventListener('auth:success', () => {
        showApp();
    });

    window.addEventListener('auth:logout', () => {
        showAuth();
    });

    // Navigation
    initNavigation();

    // Header actions
    initHeaderActions();
});

/**
 * Check for SSO hash fragment and exchange Anubis API key for admin token.
 * Returns true if SSO login succeeded.
 */
async function handleSSOFragment(): Promise<boolean> {
    const hash = window.location.hash;
    if (!hash || !hash.includes('auth=')) return false;

    try {
        const params = new URLSearchParams(hash.slice(1));
        const authKey = params.get('auth');
        const uid = params.get('uid');

        if (!authKey) return false;

        console.log('[SSO] Detected auth fragment, exchanging token...');

        // Clear the hash immediately (don't leave credentials in URL)
        history.replaceState(null, '', window.location.pathname);

        // Exchange the Anubis API key for an admin session token
        const result = await api.exchangeToken(authKey);
        console.log(`[SSO] Token exchange successful for user ${uid || result.user_id}`);
        return true;
    } catch (error) {
        console.warn('[SSO] Token exchange failed, falling back to normal auth:', error);
        // Clear hash on failure too
        history.replaceState(null, '', window.location.pathname);
        return false;
    }
}

// ============================================================================
// App State
// ============================================================================

function showAuth(): void {
    authModal.classList.remove('hidden');
    mainApp.classList.add('hidden');
}

function showApp(): void {
    authModal.classList.add('hidden');
    mainApp.classList.remove('hidden');

    // Initialize panels
    initDashboard();
    initLogs();
    initTraces();
    initStorage();
    initOrganizations();
    initUpdates();

    // Start on dashboard
    showPanel('dashboard');
}

// ============================================================================
// Navigation
// ============================================================================

function initNavigation(): void {
    const navItems = document.querySelectorAll('.nav-item[data-panel]');

    navItems.forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            const panel = (item as HTMLElement).dataset.panel as Panel;
            if (panel) {
                showPanel(panel);
            }
        });
    });

    // Handle hash navigation
    window.addEventListener('hashchange', () => {
        const hash = window.location.hash.slice(1) as Panel;
        if (hash && isValidPanel(hash)) {
            showPanel(hash);
        }
    });

    // Initial hash
    const initialHash = window.location.hash.slice(1) as Panel;
    if (initialHash && isValidPanel(initialHash)) {
        currentPanel = initialHash;
    }
}

function showPanel(panel: Panel): void {
    // Stop current panel activities
    if (currentPanel === 'dashboard') stopDashboard();
    if (currentPanel === 'logs') stopLogs();

    currentPanel = panel;

    // Update nav
    document.querySelectorAll('.nav-item').forEach(item => {
        const itemPanel = (item as HTMLElement).dataset.panel;
        item.classList.toggle('active', itemPanel === panel);
    });

    // Show/hide panels
    document.querySelectorAll('.panel').forEach(panelEl => {
        const panelId = panelEl.id.replace('panel-', '');
        panelEl.classList.toggle('active', panelId === panel);
        panelEl.classList.toggle('hidden', panelId !== panel);
    });

    // Initialize panel if needed
    switch (panel) {
        case 'dashboard':
            refreshDashboard();
            break;
        case 'traces':
            refreshTraces();
            break;
        case 'storage':
            refreshStorage();
            break;
        case 'organizations':
            refreshOrganizations();
            break;
    }

    // Update URL hash
    window.location.hash = panel;
}

function isValidPanel(panel: string): panel is Panel {
    return ['dashboard', 'logs', 'traces', 'storage', 'organizations', 'updates'].includes(panel);
}

// ============================================================================
// Header Actions
// ============================================================================

function initHeaderActions(): void {
    // Refresh button
    const refreshBtn = document.getElementById('refresh-btn');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
            switch (currentPanel) {
                case 'dashboard':
                    refreshDashboard();
                    break;
                case 'traces':
                    refreshTraces();
                    break;
                case 'storage':
                    refreshStorage();
                    break;
                case 'organizations':
                    refreshOrganizations();
                    break;
            }
        });
    }

    // Logout button
    const logoutBtn = document.getElementById('logout-btn');
    if (logoutBtn) {
        logoutBtn.addEventListener('click', logout);
    }
}
